import { Box, Skeleton } from '@mui/material';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import { SubmitEvent, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
	AgreementBusinessModel,
	AgreementServiceType,
	CreateOrUpdateServiceAgreementCommand,
	ServiceAgreementDto,
	useProductCenterApi,
} from 'api';
import {
	agreementsBusinessModelsQuery,
	agreementsServiceTypesQuery,
} from 'api/queries';
import { unitKeys } from 'api/queries/UnitQueries';
import {
	agreementBusinessModelToTranslationMapping,
	agreementServiceTypeToTranslationMapping,
} from 'api/responses/models/Unit/ServiceAgreementDto';
import { AreaName } from 'domain/constants';
import { PageButtons } from 'library/components/controls/buttons/PageButtons';
import { ControlledDatePicker } from 'library/components/controls/form/ControlledDatePicker';
import { ControlledFormattedTextField } from 'library/components/controls/form/ControlledFormattedTextField';
import { ControlledSelect } from 'library/components/controls/form/ControlledSelect';
import { ControlledSwitch } from 'library/components/controls/form/ControlledSwitch';
import { ControlledTextField } from 'library/components/controls/form/ControlledTextField';
import { PageFormContainer } from 'library/components/controls/pageTemplates/PageFormContainer';
import { NUMBER_MASK } from 'library/constants/InputMasks';
import { usePrompt } from 'library/hooks/useRouterNavigation';
import { OperationType } from 'library/models/enums/OperationType';
import { selectUser } from 'library/redux/application';
import AuthorizeComponent from 'main/auth/AuthorizeComponent';
import { checkUserAccess } from 'main/auth/checkUserAccess';
import { BusinessPartnerPicker } from 'modules/Shared/Components/BusinessPartner/BusinessPartnerPicker';

import { AnnulmentServiceAgreementButton } from './AnnulmentServiceAgreementButton';

interface ServiceAgreementFormData {
	startDate: DateTime | null;
	endDate: DateTime | null;
	reference: string;
	startHour: string | null;
	maxHour: string | null;
	preventiveMaintenance: boolean;
	genuineOil: boolean;
	preventiveRepair: boolean;
	additionalContent: string;
	tcoUsed: boolean;
	businessPartnerId: string | null;
	businessModel: AgreementBusinessModel | null;
	serviceType: AgreementServiceType | null;
	validatedByVolvoPenta: boolean;
}

interface ServiceAgreementFormProps {
	serviceAgreement?: ServiceAgreementDto | null;
	unitId: number;
	onBack: () => void;
}

export const ServiceAgreementForm = ({
	serviceAgreement,
	unitId,
	onBack,
}: ServiceAgreementFormProps): React.JSX.Element => {
	const { t } = useTranslation(['unitOverview', 'common', 'commonButton']);
	const { enqueueSnackbar } = useSnackbar();
	const queryClient = useQueryClient();
	const isEditMode = !!serviceAgreement;
	const { api } = useProductCenterApi();
	const user = useSelector(selectUser);

	const serviceTypesQuery = useQuery(agreementsServiceTypesQuery(api));
	const businessModelsQuery = useQuery(agreementsBusinessModelsQuery(api));

	const isServiceAgreementAdmin = useMemo(
		() =>
			checkUserAccess(queryClient, {
				areaName: AreaName.UnitConfiguration_ServiceAgreementAdmin,
				permission: OperationType.Write,
			}),
		[queryClient]
	);

	const serviceAgreementMutation = useMutation({
		mutationFn: (payload: CreateOrUpdateServiceAgreementCommand) =>
			api.createOrUpdateServiceAgreement(unitId.toString(), payload),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: unitKeys.activeServiceAgreementData(unitId),
			});
			await queryClient.invalidateQueries({
				queryKey: unitKeys.serviceAgreementData(unitId),
			});
			enqueueSnackbar(
				isEditMode
					? t('unitOverview:service-agreement-updated-success')
					: t('unitOverview:service-agreement-created-success'),
				{
					variant: 'success',
				}
			);
			onBack();
		},
		onError: () => {
			enqueueSnackbar(
				isEditMode
					? t('unitOverview:service-agreement-update-error')
					: t('unitOverview:service-agreement-create-error'),
				{
					variant: 'error',
				}
			);
		},
	});

	const {
		handleSubmit,
		control,
		formState: { isDirty },
		watch,
		setValue,
	} = useForm<ServiceAgreementFormData>({
		defaultValues: {
			businessPartnerId:
				serviceAgreement?.businessPartnerId ??
				user.customer?.customerNumber?.toString() ??
				null,
			reference: serviceAgreement?.reference ?? '',
			startDate: serviceAgreement?.startDate
				? DateTime.fromJSDate(new Date(serviceAgreement.startDate))
				: DateTime.now(),
			endDate: serviceAgreement?.endDate
				? DateTime.fromJSDate(new Date(serviceAgreement.endDate))
				: null,
			startHour: serviceAgreement?.startHour?.toString() ?? null,
			maxHour: serviceAgreement?.maxHour?.toString() ?? null,
			preventiveMaintenance:
				serviceAgreement?.preventiveMaintenance ?? false,
			genuineOil: serviceAgreement?.genuineOil ?? false,
			preventiveRepair: serviceAgreement?.preventiveRepair ?? false,
			tcoUsed: serviceAgreement?.tcoUsed ?? false,
			additionalContent: serviceAgreement?.additionalContent ?? '',
			businessModel: serviceAgreement?.businessModel ?? null,
			serviceType: serviceAgreement?.serviceType ?? null,
			validatedByVolvoPenta:
				serviceAgreement?.validatedByVolvoPenta ?? false,
		},
	});

	// Watch fields for conditional logic
	const watchServiceType = watch('serviceType');
	const watchBusinessModel = watch('businessModel');

	// Clear Business Model when Service Type is SLA
	useEffect(() => {
		if (watchServiceType === 'SLA') {
			setValue('businessModel', null);
		}
	}, [watchServiceType, setValue]);

	// Clear ValidatedByVolvoPenta when Business Model is not BM1
	useEffect(() => {
		if (watchBusinessModel !== 'BM1') {
			setValue('validatedByVolvoPenta', false);
		}
	}, [watchBusinessModel, setValue]);

	// Computed values for conditional rendering
	const isBusinessModelDisabled = watchServiceType === 'SLA';
	const shouldShowValidatedCheckbox =
		watchBusinessModel === 'BM1' && isServiceAgreementAdmin;

	const Prompt = usePrompt(
		t('common:unsaved-warning-dialog-message'),
		isDirty
	);

	const handleFormSubmit = (data: ServiceAgreementFormData) => {
		if (data.startDate == null || data.endDate == null) {
			enqueueSnackbar(t('common:validation-error-message'), {
				variant: 'error',
			});
			return;
		}
		const payload: CreateOrUpdateServiceAgreementCommand = {
			...data,
			id: serviceAgreement?.id,
			startDate: data.startDate.toJSDate(),
			endDate: data.endDate.toJSDate(),
			startHour: data.startHour ? parseInt(data.startHour, 10) : null,
			maxHour: data.maxHour ? parseInt(data.maxHour, 10) : null,
		};

		serviceAgreementMutation.mutate(payload);
	};

	const onSubmit = (e: SubmitEvent<HTMLFormElement>) => {
		e.preventDefault();
		void handleSubmit(handleFormSubmit, (err) => {
			console.error(err);
			enqueueSnackbar(t('common:validation-error-message'), {
				variant: 'error',
			});
		})(e);
	};

	const isSubmitDisabled = serviceAgreementMutation.isPending;

	const businessPartnerInitialInputValue = (() => {
		if (
			serviceAgreement?.businessPartnerId &&
			serviceAgreement?.businessPartnerName
		) {
			return `${serviceAgreement.businessPartnerId} - ${serviceAgreement.businessPartnerName}`;
		}
		if (serviceAgreement?.businessPartnerName) {
			return serviceAgreement.businessPartnerName;
		}
		if (user.customer?.customerNumber && user.customer?.customerName) {
			return `${user.customer.customerNumber} - ${user.customer.customerName}`;
		}
		return user.customer?.customerName ?? '';
	})();

	return (
		<PageFormContainer noValidate onSubmit={onSubmit}>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}>
				<Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 600 }}>
					{isEditMode
						? t('unitOverview:service-agreement-edit-title')
						: t('unitOverview:service-agreement-add-title')}
				</Typography>
				{serviceAgreement?.id && (
					<AnnulmentServiceAgreementButton
						unitId={unitId}
						agreementId={serviceAgreement.id}
						onSuccess={onBack}
					/>
				)}
			</Box>
			<Paper
				variant="flatGrey"
				sx={(theme) => ({
					p: 2,
					display: 'flex',
					gap: 10,
					[theme.breakpoints.down('sm')]: {
						flexDirection: 'column',
						gap: 2,
					},

					'.MuiInputBase-root.MuiOutlinedInput-root, .MuiPickersInputBase-root.MuiPickersOutlinedInput-root':
						{
							backgroundColor: 'background.paper',
						},
				})}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					<ControlledDatePicker
						name="startDate"
						control={control}
						required
						label={t('unitOverview:service-agreement-start-date')}
						size="small"
						helperText={t(
							'unitOverview:service-agreement-start-date-helper'
						)}
						validation={{
							required: t('common:validation-error-message'),
						}}
					/>
					<ControlledDatePicker
						name="endDate"
						control={control}
						required
						label={t('common:end-date')}
						size="small"
						helperText={t(
							'unitOverview:service-agreement-end-date-helper'
						)}
						validation={{
							required: t('common:validation-error-message'),
							validate: (value, { startDate }) => {
								if (
									value != null &&
									startDate != null &&
									value < startDate
								) {
									return t(
										'unitOverview:service-agreement-validation-end-date-bigger-than-start-date'
									);
								}
								return true;
							},
						}}
					/>
					<ControlledFormattedTextField
						name="startHour"
						control={control}
						label={t('unitOverview:service-agreement-start-hour')}
						type="tel"
						size="small"
						imaskProps={{
							mask: NUMBER_MASK,
						}}
						helperText={t(
							'unitOverview:service-agreement-start-hour-helper'
						)}
					/>
					<ControlledFormattedTextField
						name="maxHour"
						control={control}
						label={t('unitOverview:service-agreement-end-max-hour')}
						type="tel"
						size="small"
						imaskProps={{
							mask: NUMBER_MASK,
						}}
						helperText={t(
							'unitOverview:service-agreement-end-max-hour-helper'
						)}
					/>
					{!serviceTypesQuery.isLoading ? (
						<ControlledSelect
							name="serviceType"
							control={control}
							label={t(
								'unitOverview:service-agreement-service-type'
							)}
							options={
								serviceTypesQuery.data?.map((d) => ({
									id: d.id,
									label: t(
										agreementServiceTypeToTranslationMapping[
											d.label as AgreementServiceType
										]
									),
								})) ?? []
							}
							size="small"
						/>
					) : (
						<Skeleton variant="rectangular" />
					)}
					{!businessModelsQuery.isLoading ? (
						<ControlledSelect
							name="businessModel"
							control={control}
							label={t(
								'unitOverview:service-agreement-business-model'
							)}
							options={
								businessModelsQuery.data?.map((d) => {
									console.log('Business Model option:', d);
									return {
										id: d.label as AgreementBusinessModel,
										label: t(
											agreementBusinessModelToTranslationMapping[
												d.label as AgreementBusinessModel
											]
										),
									};
								}) ?? []
							}
							size="small"
							disabled={isBusinessModelDisabled}
						/>
					) : (
						<Skeleton variant="rectangular" />
					)}
					<ControlledTextField
						name="reference"
						control={control}
						label={t('unitOverview:service-agreement-reference')}
						size="small"
						helperText={t(
							'unitOverview:service-agreement-reference-helper'
						)}
					/>
					<BusinessPartnerPicker<ServiceAgreementFormData>
						id="service-agreement-business-partner"
						control={control}
						name={'businessPartnerId'}
						label={t(
							'unitOverview:responsible-business-partner-label'
						)}
						helperText={t(
							'unitOverview:service-agreement-business-partner-helper'
						)}
						initialInputValue={businessPartnerInitialInputValue}
						size="small"
						disabled={!isServiceAgreementAdmin}
					/>
					<ControlledTextField
						name="additionalContent"
						control={control}
						label={t(
							'unitOverview:service-agreement-additional-information'
						)}
						multiline
						rows={4}
					/>
				</Box>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					<ControlledSwitch
						name="preventiveMaintenance"
						control={control}
						label={t(
							'unitOverview:service-agreement-preventive-maintenance'
						)}
						helperText={t(
							'unitOverview:service-agreement-preventive-maintenance-helper'
						)}
					/>
					<ControlledSwitch
						name="genuineOil"
						control={control}
						label={t('unitOverview:service-agreement-genuine-oil')}
						helperText={t(
							'unitOverview:service-agreement-genuine-oil-helper'
						)}
					/>
					<ControlledSwitch
						name="preventiveRepair"
						control={control}
						label={t(
							'unitOverview:service-agreement-preventive-repair'
						)}
						helperText={t(
							'unitOverview:service-agreement-preventive-repair-helper'
						)}
					/>
					<ControlledSwitch
						name="tcoUsed"
						control={control}
						label={t(
							'unitOverview:service-agreement-service-calculator'
						)}
						helperText={t(
							'unitOverview:service-agreement-service-calculator-helper'
						)}
					/>
					{shouldShowValidatedCheckbox && (
						<AuthorizeComponent
							areaName={
								AreaName.UnitConfiguration_ServiceAgreementAdmin
							}
							permission={OperationType.Write}>
							<ControlledSwitch
								name="validatedByVolvoPenta"
								control={control}
								label={t(
									'unitOverview:service-agreement-validated-by-volvo-penta'
								)}
								helperText={t(
									'unitOverview:service-agreement-validated-helper'
								)}
								disabled={!isServiceAgreementAdmin}
							/>
						</AuthorizeComponent>
					)}
				</Box>
			</Paper>
			<PageButtons
				primaryButtonLabel={t('commonButton:save')}
				secondaryButtonLabel={t('commonButton:back')}
				secondaryButtonAction={onBack}
				primaryButtonDisabled={isSubmitDisabled}
				isPrimaryButtonSubmitType
			/>
			{Prompt}
		</PageFormContainer>
	);
};
